import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Tooltip } from '@ui/Tooltip';
import { Icon } from '@ui/Icon';
import { SelectTrigger } from '@ui/SelectTrigger';
import { Menu } from '@ui/menus/Menu';
import { getKeys } from '@shared/helpers/object.helpers';

export const ListSortControl = <T extends string>({
  value,
  options,
  onChange,
  direction,
  onDirectionChange,
  preserveOptionOrder = false,
}: {
  value: T;
  options: Partial<Record<T, string>>;
  onChange: (value: T) => void;
  direction?: 'asc' | 'desc';
  onDirectionChange?: (direction: 'asc' | 'desc') => void;
  preserveOptionOrder?: boolean;
}) => {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [hoveredOption, setHoveredOption] = useState<T | null>(null);
  const [tooltipAnchor, setTooltipAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Tooltip title="Sort by">
        <Box component="span" sx={{ display: 'flex' }}>
          <SelectTrigger
            icon={
              <Icon
                category="mui"
                icon={
                  direction
                    ? direction === 'asc'
                      ? 'ArrowUpward'
                      : 'ArrowDownward'
                    : 'SwapVert'
                }
                size={12}
              />
            }
            value={options[value] ?? value}
            open={Boolean(anchor)}
            onClick={(e) => {
              setHoveredOption(null);
              setAnchor(e.currentTarget);
            }}
          />
        </Box>
      </Tooltip>
      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
          setHoveredOption(null);
        }}
      >
        <Menu.Group label="Sort by">
          {getKeys(options, !preserveOptionOrder).map((option) => {
            const nextDirection =
              value === option && direction === 'desc' ? 'asc' : 'desc';
            const displayedDirection =
              hoveredOption === option ? nextDirection : direction;
            return (
              <Menu.Item
                key={option}
                selected={value === option}
                onMouseEnter={() => setHoveredOption(option)}
                onMouseLeave={() => setHoveredOption(null)}
                icon={
                  value === option ? (
                    <Icon
                      category="mui"
                      icon={
                        onDirectionChange
                          ? displayedDirection === 'asc'
                            ? 'ArrowUpward'
                            : 'ArrowDownward'
                          : 'Check'
                      }
                    />
                  ) : undefined
                }
                onClick={() => {
                  onChange(option);
                  onDirectionChange?.(nextDirection);
                  setAnchor(null);
                }}
              >
                {options[option]}
                {onDirectionChange && (
                  <Menu.Item.Action>
                    {(['desc', 'asc'] as const).map((arrowDirection) => (
                      <Tooltip
                        key={arrowDirection}
                        title={
                          arrowDirection === 'desc'
                            ? 'Sort descending'
                            : 'Sort ascending'
                        }
                        placement="right"
                        anchorEl={tooltipAnchor}
                        allowWhileMenuOpen
                      >
                        <Box
                          component="button"
                          type="button"
                          data-sort-direction={arrowDirection}
                          aria-label={`${arrowDirection === 'desc' ? 'Sort descending' : 'Sort ascending'} by ${options[option]}`}
                          onMouseEnter={(event) => {
                            const row =
                              event.currentTarget.closest('[data-menu-index]');
                            if (row instanceof HTMLElement)
                              setTooltipAnchor(row);
                          }}
                          onKeyDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                            event.stopPropagation();
                            onChange(option);
                            onDirectionChange(arrowDirection);
                            setAnchor(null);
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 20,
                            height: 20,
                            p: 0,
                            border: 'none',
                            background: 'none',
                            color:
                              arrowDirection === nextDirection
                                ? 'rgba(0,0,0,0.8)'
                                : 'rgba(0,0,0,0.3)',
                            [`[data-menu-trailing="action"]:has([data-sort-direction="${arrowDirection === 'desc' ? 'asc' : 'desc'}"]:hover) &`]:
                              {
                                color: 'rgba(0,0,0,0.3)',
                              },
                            cursor: 'pointer',
                            '&:hover, &:focus-visible': {
                              color: 'rgba(0,0,0,1)',
                            },
                          }}
                        >
                          <Icon
                            category="mui"
                            icon={
                              arrowDirection === 'desc'
                                ? 'ArrowDownward'
                                : 'ArrowUpward'
                            }
                            size={12}
                          />
                        </Box>
                      </Tooltip>
                    ))}
                  </Menu.Item.Action>
                )}
              </Menu.Item>
            );
          })}
        </Menu.Group>
      </Menu>
    </>
  );
};
